import React, { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Check, Sparkles, Plus, Minus } from "lucide-react";
import { format } from "date-fns";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarUI } from "@/components/ui/calendar";
import { api } from "@/lib/api";

const SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

const CREWS = [
  { key: "1man", label: "1 Man + Van", rate: 40 },
  { key: "2men", label: "2 Men + Van", rate: 60 },
];
const HELPER_RATE = 20;
const CALL_OUT = 35;
const MILE_RATE = 1.5;
const FREE_MILES = 10;
const MIN_HOURS = 2;

function calcPrice({ crew, helpers, hours, miles }) {
  const crewObj = CREWS.find((c) => c.key === crew) || CREWS[0];
  const chargeableHours = Math.max(MIN_HOURS, hours);
  const chargeableMiles = Math.max(0, miles - FREE_MILES);
  const hourly = crewObj.rate + helpers * HELPER_RATE;
  const labour = hourly * chargeableHours;
  const mileage = chargeableMiles * MILE_RATE;
  const total = Math.round(CALL_OUT + labour + mileage);
  return {
    total,
    hourly,
    chargeableHours,
    chargeableMiles,
    breakdown: {
      callOut: CALL_OUT,
      labour: Math.round(labour),
      mileage: Math.round(mileage),
    },
  };
}

export default function Estimator() {
  const [step, setStep] = useState(0);
  const [crew, setCrew] = useState("2men");
  const [helpers, setHelpers] = useState(0);
  const [hours, setHours] = useState(3);
  const [miles, setMiles] = useState(15);
  const [date, setDate] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);

  const { total, hourly, chargeableHours, chargeableMiles, breakdown } = useMemo(
    () => calcPrice({ crew, helpers, hours, miles }),
    [crew, helpers, hours, miles]
  );

  const next = () => setStep((s) => Math.min(s + 1, 4));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    if (SITE_KEY && !captchaToken) {
      toast.error("Please complete the captcha.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/estimates", {
        email,
        crew,
        helpers,
        hours: chargeableHours,
        distance_miles: miles,
        move_date: date ? format(date, "yyyy-MM-dd") : null,
        price: total,
        captcha_token: captchaToken,
      });
      setStep(4);
      toast.success("We've sent your estimate — check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please try again shortly.");
      captchaRef.current?.resetCaptcha();
      setCaptchaToken("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="relative bg-[var(--jb-bone)] border-y border-[var(--jb-line)]"
      data-testid="estimator-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          {/* LEFT — heading */}
          <div className="md:col-span-4">
            <div className="mono-cap text-[var(--jb-rust)] mb-4 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Instant estimator
            </div>
            <h2 className="serif text-[clamp(32px,4vw,56px)] leading-[1] tracking-tight text-[var(--jb-navy)]">
              Ballpark price. <br />
              <span className="italic">In 20 seconds.</span>
            </h2>
            <p className="mt-6 text-[15px] leading-[1.6] text-[var(--jb-muted)] max-w-[380px]">
              A transparent, indicative price before you speak to us. Your full quote is always confirmed by a human.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 mono-cap text-[11px] text-[var(--jb-navy)]">
              <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-[var(--jb-rust)]" /> Fully insured</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-[var(--jb-rust)]" /> No booking fee</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-[var(--jb-rust)]" /> First 10 miles free</span>
            </div>
            <div className="mt-8 border-t border-[var(--jb-line)] pt-6 space-y-2 text-[13px] text-[var(--jb-muted)]">
              <div className="mono-cap text-[10px] text-[var(--jb-navy)] mb-2">Our rates</div>
              <div className="flex justify-between"><span>Call-out</span><span className="text-[var(--jb-navy)]">£35</span></div>
              <div className="flex justify-between"><span>Mileage (after 10mi)</span><span className="text-[var(--jb-navy)]">£1.50 / mile</span></div>
              <div className="flex justify-between"><span>1 Man + Van</span><span className="text-[var(--jb-navy)]">£40 / hour</span></div>
              <div className="flex justify-between"><span>2 Men + Van</span><span className="text-[var(--jb-navy)]">£60 / hour</span></div>
              <div className="flex justify-between"><span>Additional helper</span><span className="text-[var(--jb-navy)]">+£20 / hour</span></div>
              <div className="flex justify-between"><span>Minimum booking</span><span className="text-[var(--jb-navy)]">2 hours</span></div>
            </div>
          </div>

          {/* RIGHT — interactive card */}
          <div className="md:col-span-8">
            <div className="relative bg-[var(--jb-navy)] text-[var(--jb-bone)] p-8 md:p-12 overflow-hidden" data-testid="estimator-card">
              <div className="grain absolute inset-0 pointer-events-none" />

              {/* Progress */}
              <div className="flex items-center gap-2 mb-8 relative z-10">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`h-[3px] flex-1 transition-all duration-500 ${
                      i <= Math.min(step, 3) ? "bg-[var(--jb-rust)]" : "bg-[var(--jb-bone)]/20"
                    }`}
                  />
                ))}
              </div>

              <div className="grid md:grid-cols-12 gap-10 items-end relative z-10">
                {/* Steps */}
                <div className="md:col-span-7 min-h-[240px]">
                  <AnimatePresence mode="wait">
                    {step === 0 && (
                      <motion.div key="s0" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
                        <div className="mono-cap text-[var(--jb-bone)]/60 mb-4">Step 01 · Crew</div>
                        <div className="grid grid-cols-2 gap-2 mb-6">
                          {CREWS.map((c) => (
                            <button
                              key={c.key}
                              onClick={() => setCrew(c.key)}
                              className={`text-left px-4 py-4 border transition-all ${
                                crew === c.key
                                  ? "bg-[var(--jb-bone)] text-[var(--jb-navy)] border-[var(--jb-bone)]"
                                  : "border-[var(--jb-bone)]/25 hover:border-[var(--jb-rust)]"
                              }`}
                              data-testid={`crew-${c.key}`}
                            >
                              <div className="serif text-[19px]">{c.label}</div>
                              <div className="mono-cap text-[10px] mt-1 opacity-70">£{c.rate}/hour</div>
                            </button>
                          ))}
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="mono-cap text-[var(--jb-bone)]/60 mb-1">Additional helpers</div>
                            <div className="text-[12px] text-[var(--jb-bone)]/50">+£20/hour each</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => setHelpers(Math.max(0, helpers - 1))}
                              className="w-9 h-9 grid place-items-center border border-[var(--jb-bone)]/25 hover:border-[var(--jb-rust)]"
                              data-testid="helpers-minus"
                              aria-label="Fewer helpers"
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="serif text-2xl w-8 text-center" data-testid="helpers-count">{helpers}</span>
                            <button
                              type="button"
                              onClick={() => setHelpers(Math.min(3, helpers + 1))}
                              className="w-9 h-9 grid place-items-center border border-[var(--jb-bone)]/25 hover:border-[var(--jb-rust)]"
                              data-testid="helpers-plus"
                              aria-label="More helpers"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {step === 1 && (
                      <motion.div key="s1" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
                        <div className="mono-cap text-[var(--jb-bone)]/60 mb-4">Step 02 · Hours needed</div>
                        <div className="flex items-baseline gap-3 mb-6">
                          <span className="serif text-6xl md:text-7xl">{chargeableHours}</span>
                          <span className="mono-cap">hours</span>
                        </div>
                        <input
                          type="range"
                          min={2}
                          max={12}
                          value={hours}
                          onChange={(e) => setHours(Number(e.target.value))}
                          className="w-full accent-[var(--jb-rust)]"
                          data-testid="hours-slider"
                        />
                        <div className="flex justify-between mt-2 mono-cap text-[10px] text-[var(--jb-bone)]/50">
                          <span>Min · 2h</span>
                          <span>All-day · 12h</span>
                        </div>
                        <p className="mt-4 text-[12px] text-[var(--jb-bone)]/60">
                          Minimum booking is 2 hours — we'll always confirm the exact time needed after a quick chat.
                        </p>
                      </motion.div>
                    )}

                    {step === 2 && (
                      <motion.div key="s2" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
                        <div className="mono-cap text-[var(--jb-bone)]/60 mb-4">Step 03 · Distance</div>
                        <div className="flex items-baseline gap-3 mb-6">
                          <span className="serif text-6xl md:text-7xl">{miles}</span>
                          <span className="mono-cap">miles</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={400}
                          value={miles}
                          onChange={(e) => setMiles(Number(e.target.value))}
                          className="w-full accent-[var(--jb-rust)]"
                          data-testid="miles-slider"
                        />
                        <div className="flex justify-between mt-2 mono-cap text-[10px] text-[var(--jb-bone)]/50">
                          <span>Local · 0mi</span>
                          <span>Cross-UK · 400mi</span>
                        </div>
                        <p className="mt-4 text-[12px] text-[var(--jb-bone)]/60">
                          The first 10 miles are free — you'll only be charged £1.50/mile after that
                          {miles > FREE_MILES ? ` (chargeable: ${chargeableMiles} miles)` : ""}.
                        </p>
                      </motion.div>
                    )}

                    {step === 3 && (
                      <motion.div key="s3" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
                        <div className="mono-cap text-[var(--jb-bone)]/60 mb-4">Almost there · Send my estimate</div>

                        <div className="mb-5">
                          <label className="mono-cap text-[10px] text-[var(--jb-bone)]/60">Preferred date (optional)</label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                className="w-full text-left border-b border-[var(--jb-bone)]/25 hover:border-[var(--jb-rust)] py-3 serif text-xl transition-colors"
                                data-testid="est-date-btn"
                              >
                                {date ? format(date, "EEEE, d MMMM yyyy") : "Choose a date →"}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 bg-white border border-[var(--jb-line)]" align="start">
                              <CalendarUI mode="single" selected={date} onSelect={setDate} initialFocus />
                            </PopoverContent>
                          </Popover>
                        </div>

                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full bg-transparent border-0 border-b border-[var(--jb-bone)]/30 focus:border-[var(--jb-rust)] py-4 text-2xl serif text-[var(--jb-bone)] placeholder:text-[var(--jb-bone)]/40 focus:outline-none"
                          data-testid="est-email"
                        />
                        {SITE_KEY && (
                          <div className="mt-5" data-testid="est-captcha">
                            <HCaptcha
                              ref={captchaRef}
                              sitekey={SITE_KEY}
                              onVerify={(t) => setCaptchaToken(t)}
                              onExpire={() => setCaptchaToken("")}
                              onError={() => setCaptchaToken("")}
                              theme="dark"
                            />
                          </div>
                        )}
                        <p className="mt-3 mono-cap text-[10px] text-[var(--jb-bone)]/50">
                          We'll send your indicative price and follow up personally. No spam.
                        </p>
                      </motion.div>
                    )}

                    {step === 4 && (
                      <motion.div key="s4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                        <div className="mono-cap text-[var(--jb-rust)] mb-4">Estimate sent ✓</div>
                        <div className="serif text-3xl md:text-4xl leading-tight">
                          Thanks — check your inbox.<br />
                          <span className="italic text-[var(--jb-rust)]">We'll be in touch.</span>
                        </div>
                        <a href="#quote" className="mt-6 inline-flex items-center gap-2 mono-cap link-underline text-[var(--jb-bone)]">
                          Or request a full quote now <ArrowUpRight className="w-3.5 h-3.5" />
                        </a>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Live Price */}
                <div className="md:col-span-5 md:border-l md:border-[var(--jb-bone)]/15 md:pl-10">
                  <div className="mono-cap text-[var(--jb-bone)]/50 mb-3">Indicative from</div>
                  <div className="flex items-baseline gap-2">
                    <span className="serif text-[26px] text-[var(--jb-rust)] mr-1">£</span>
                    <motion.span
                      key={total}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                      className="serif text-[clamp(56px,7vw,104px)] leading-none tracking-tight"
                      data-testid="live-price"
                    >
                      {total}
                    </motion.span>
                  </div>
                  <div className="mt-4 text-[12px] text-[var(--jb-bone)]/60 space-y-1 mono-cap">
                    <div className="flex justify-between"><span>Call-out</span><span>£{breakdown.callOut}</span></div>
                    <div className="flex justify-between">
                      <span>Labour ({chargeableHours}h × £{hourly})</span>
                      <span>£{breakdown.labour}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Mileage ({chargeableMiles}mi × £1.50)</span>
                      <span>£{breakdown.mileage}</span>
                    </div>
                  </div>
                  {helpers > 0 && (
                    <div className="mt-3 mono-cap text-[10px] text-[var(--jb-bone)]/50">
                      Includes {helpers} helper{helpers > 1 ? "s" : ""} @ +£20/hour
                    </div>
                  )}
                </div>
              </div>

              {/* Nav */}
              {step < 4 && (
                <div className="mt-10 flex items-center justify-between relative z-10">
                  <button
                    onClick={back}
                    disabled={step === 0}
                    className="mono-cap text-[var(--jb-bone)]/60 disabled:opacity-30"
                    data-testid="est-back"
                  >
                    ← Back
                  </button>
                  {step < 3 ? (
                    <button
                      onClick={next}
                      className="cta-primary bg-[var(--jb-rust)] text-[var(--jb-bone)] mono-cap px-7 py-3.5 inline-flex items-center gap-2"
                      data-testid="est-next"
                    >
                      {step === 2 ? "See my price" : "Next"}
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button
                      onClick={submit}
                      disabled={loading}
                      className="cta-primary bg-[var(--jb-rust)] text-[var(--jb-bone)] mono-cap px-7 py-3.5 inline-flex items-center gap-2 disabled:opacity-60"
                      data-testid="est-submit"
                    >
                      {loading ? "Sending…" : "Send estimate"}
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
